import { on } from 'js/lib/events'
import { wait, debounce } from 'js/lib/general-utils'
import { registerFunctionalComponent } from 'js/lib/components/component'
import { useListener } from 'js/lib/dynamic/listener'
import { requestNative } from 'js/lib/api/api-request'
import { turbo } from 'js/lib/turbo'
import { notifyBugsnag } from 'js/lib/bugsnag'

// Global time to wait before executing another fetch
const GLOBAL_EVENT_DEBOUNCE = 50
// How long to wait on the network request before showing the fallback UI (if it exists)
const FALLBACK_DELAY = 250
// How long to show the fallback for if it gets shown
const MIN_FALLBACK_VISIBLE = 1000

registerFunctionalComponent('.js-c-frame', 'DynamicFrame', DynamicFrame)

function DynamicFrame(component) {
  const listenerAst = JSON.parse(component.element.dataset.trigger)

  const { onChange } = useListener(listenerAst)

  const closestForm = () => component.element.closest('form')
  const fallbackElement = () => component.element.querySelector('.js-c-frame-fallback')
  const errorElement = () => component.element.querySelector('.js-c-frame-error')
  const errorReloadButton = () => errorElement().querySelector('.js-frame-reload')
  const contentElement = () => component.element.querySelector('.js-c-frame-content')

  const handleRefreshTrigger = debounce(() => refresh(), GLOBAL_EVENT_DEBOUNCE, true)

  component.useHandler(() => onChange(handleRefreshTrigger))
  component.useHandler(() => on('click', errorReloadButton, handleRefreshTrigger))

  // Refreshes the frame by submiting an ajax request to the most appropriate url (the frame src,
  // the nearest form, or the window location).
  async function refresh() {
    const refreshFn = closestForm() ? refreshByFormSubmit : refreshByGet

    let fallbackShownAt = null

    const fallbackTimer = setTimeout(() => {
      // show the fallback and hide the error to make the experience nice
      fallbackShownAt = Date.now()
      showFallback()
      hideError()
    }, FALLBACK_DELAY)

    try {
      // refresh handlers now return a "commit" function which applies the result
      const commit = await refreshFn()
      clearTimeout(fallbackTimer)

      if (fallbackShownAt) {
        const elapsed = Date.now() - fallbackShownAt
        if (elapsed < MIN_FALLBACK_VISIBLE) await wait(MIN_FALLBACK_VISIBLE - elapsed)
        hideFallback()
      }

      // apply the refresh side-effect (reload / render stream). We also re-mount the component
      // (unless it has been removed from the DOM) so that all our element references stay fresh
      commit()
    } catch (error) {
      notifyBugsnag(error)
      clearTimeout(fallbackTimer)

      // Hide the fallback if there is one and show the error
      hideFallback()
      showError()
    }
  }

  async function refreshByGet() {
    return await refreshRequest(undefined, 'GET')
  }

  // Refreshes by submitting the form - if the form method is set, use that
  // as the request method.
  async function refreshByFormSubmit() {
    const formData = new FormData(closestForm())
    const reqType = (formData.get('_method') || 'POST').toUpperCase()

    return await refreshRequest(formData, reqType)
  }

  // Rails requires a special request format for turbo streams. We return a commit function
  // that will render the stream when called so we can orchestrate showing/hiding the fallback
  async function refreshRequest(data, type) {
    const html = await requestNative({
      url: getFrameUrl(),
      type,
      data,
      accept: 'text/vnd.turbo-stream.html',
    })

    return () => turbo.renderStreamMessage(html)
  }

  function getFrameUrl() {
    const srcUrl = component.element.dataset.src
    if (srcUrl) return srcUrl

    const form = component.element.closest('form')

    if (form) return form.getAttribute('action') || window.location.href
    return window.location.href
  }

  function showFallback() {
    const fallback = fallbackElement()
    if (!fallback) return

    hideContent()
    showElement(fallback)
  }

  function hideFallback() {
    const fallback = fallbackElement()
    if (!fallback) return

    hideElement(fallback)
    showContent()
  }

  function showError() {
    showElement(errorElement())
  }

  function hideError() {
    hideElement(errorElement())
  }

  function showContent() {
    showElement(contentElement())
  }

  function hideContent() {
    hideElement(contentElement())
  }
}

function showElement(el) {
  if (!el) return
  el.style.display = ''
  el.removeAttribute('aria-hidden')
}

function hideElement(el) {
  if (!el) return
  el.style.display = 'none'
  el.setAttribute('aria-hidden', 'true')
}
